import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Play, Pause, X, Loader2, Upload } from "lucide-react";
import {
  uploadProjectAsset,
  getAssetSignedUrl,
} from "../utils/projectAssets";

const MAX_DURATION_SEC = 60;
const ALLOWED_EXT = [".mp3", ".m4a", ".wav", ".ogg", ".webm"];
const ALLOWED_MIME_PREFIXES = ["audio/"];

export interface DialogueAudioValue {
  url: string;
  storagePath: string;
  durationSec: number;
  assetId: string;
}

export interface DialogueAudioInputProps {
  value: DialogueAudioValue | null;
  onChange: (value: DialogueAudioValue | null) => void;
  projectId: string | null;
  companyId: string | null;
  userId?: string | null;
  disabled?: boolean;
  /** Oculta la opción "Subir un archivo" (deja solo grabar). */
  hideUpload?: boolean;
}

type RecorderState = "idle" | "recording" | "uploading" | "ready" | "error";

function formatDuration(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return "0:00";
  const s = Math.floor(seconds);
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${m}:${ss.toString().padStart(2, "0")}`;
}

async function probeAudioDuration(file: File | Blob): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    audio.preload = "metadata";
    audio.onloadedmetadata = () => {
      const d = audio.duration;
      URL.revokeObjectURL(url);
      resolve(d);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("No se pudo leer la duración del audio"));
    };
    audio.src = url;
  });
}

const DialogueAudioInput: React.FC<DialogueAudioInputProps> = ({
  value,
  onChange,
  projectId,
  companyId,
  userId,
  disabled,
  hideUpload,
}) => {
  const [state, setState] = useState<RecorderState>(value ? "ready" : "idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const recordingStartedAtRef = useRef<number>(0);
  const recordingTimerRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const playerRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      stopAndCleanupRecording();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setState(value ? "ready" : "idle");
  }, [value?.url]);

  const stopAndCleanupRecording = () => {
    if (recordingTimerRef.current != null) {
      window.clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    try {
      mediaRecorderRef.current?.state !== "inactive" &&
        mediaRecorderRef.current?.stop();
    } catch {}
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  };

  const handleStartRecording = async () => {
    setErrorMessage(null);

    if (!projectId || !companyId) {
      setErrorMessage(
        "Falta el proyecto o la empresa para guardar el audio. Recargá la página.",
      );
      setState("error");
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      setErrorMessage(
        "Necesitamos permiso al micrófono para grabar. Habilitalo en la configuración del navegador o subí un archivo.",
      );
      setState("error");
      return;
    }

    mediaStreamRef.current = stream;

    let mimeType = "audio/webm";
    if (
      typeof MediaRecorder !== "undefined" &&
      !MediaRecorder.isTypeSupported(mimeType)
    ) {
      mimeType = "audio/mp4";
    }

    const mr = new MediaRecorder(stream, { mimeType });
    mediaRecorderRef.current = mr;
    chunksRef.current = [];

    mr.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    mr.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
      if (recordingTimerRef.current != null) {
        window.clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      if (blob.size === 0) {
        setErrorMessage("La grabación quedó vacía, probá de nuevo.");
        setState("error");
        return;
      }
      await handleUploadBlob(blob, mimeType);
    };

    mr.start();
    recordingStartedAtRef.current = Date.now();
    setRecordingDuration(0);
    setState("recording");

    recordingTimerRef.current = window.setInterval(() => {
      const elapsed = (Date.now() - recordingStartedAtRef.current) / 1000;
      setRecordingDuration(elapsed);
      if (elapsed >= MAX_DURATION_SEC) {
        handleStopRecording();
      }
    }, 100);
  };

  const handleStopRecording = () => {
    try {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === "recording"
      ) {
        mediaRecorderRef.current.stop();
      }
    } catch {}
  };

  const handleCancelRecording = () => {
    if (recordingTimerRef.current != null) {
      window.clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    try {
      mediaRecorderRef.current?.stream
        ?.getTracks()
        .forEach((t) => t.stop());
    } catch {}
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    setRecordingDuration(0);
    setState("idle");
  };

  const handleFilePick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = async (
    e,
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setErrorMessage(null);

    const lowerName = file.name.toLowerCase();
    const hasAllowedExt = ALLOWED_EXT.some((ext) => lowerName.endsWith(ext));
    const hasAllowedMime = ALLOWED_MIME_PREFIXES.some((p) =>
      file.type.startsWith(p),
    );
    if (!hasAllowedExt && !hasAllowedMime) {
      setErrorMessage(
        "Formato no soportado. Subí un archivo .mp3, .m4a, .wav o .ogg.",
      );
      setState("error");
      return;
    }

    let dur: number;
    try {
      dur = await probeAudioDuration(file);
    } catch {
      setErrorMessage("No pudimos leer el audio. Probá con otro archivo.");
      setState("error");
      return;
    }

    if (!isFinite(dur) || dur <= 0) {
      setErrorMessage("El audio no tiene duración válida.");
      setState("error");
      return;
    }

    if (dur > MAX_DURATION_SEC) {
      setErrorMessage(
        `El audio dura ${formatDuration(dur)}, supera el límite de ${MAX_DURATION_SEC}s. Recortalo o dividilo en varias escenas.`,
      );
      setState("error");
      return;
    }

    await handleUploadFile(file);
  };

  const handleUploadFile = async (file: File) => {
    if (!projectId || !companyId) {
      setErrorMessage(
        "Falta el proyecto o la empresa para guardar el audio. Recargá la página.",
      );
      setState("error");
      return;
    }

    setState("uploading");
    try {
      const asset = await uploadProjectAsset({
        file,
        projectId,
        companyId,
        userId: userId ?? null,
      });

      const url = await getAssetSignedUrl(asset.storage_path);
      if (!url) throw new Error("No pudimos firmar la URL del audio.");

      const durationSec =
        asset.duration_seconds ?? (await probeAudioDuration(file).catch(() => 0));

      onChange({
        url,
        storagePath: asset.storage_path,
        durationSec,
        assetId: asset.id,
      });
      setState("ready");
    } catch (err: any) {
      setErrorMessage(err?.message || "Falló la subida del audio.");
      setState("error");
    }
  };

  const handleUploadBlob = async (blob: Blob, mimeType: string) => {
    const ext = mimeType.includes("mp4") ? "m4a" : "webm";
    const file = new File(
      [blob],
      `grabacion_${Date.now()}.${ext}`,
      { type: mimeType },
    );
    await handleUploadFile(file);
  };

  const handleClear = () => {
    if (playerRef.current) {
      try {
        playerRef.current.pause();
      } catch {}
    }
    setIsPlaying(false);
    setErrorMessage(null);
    onChange(null);
    setState("idle");
  };

  const togglePlay = () => {
    const el = playerRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => {});
      setIsPlaying(true);
    } else {
      el.pause();
      setIsPlaying(false);
    }
  };

  const isNearLimit = recordingDuration >= MAX_DURATION_SEC - 10;

  return (
    <div className="space-y-3">
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_EXT.join(",") + ",audio/*"}
        className="hidden"
        onChange={handleFileChange}
      />

      {/* IDLE state */}
      {state === "idle" && (
        <div
          className={`bg-white border border-neutral-200 rounded-lg p-5 ${disabled ? "opacity-60 pointer-events-none" : ""}`}
        >
          <div className="flex flex-col items-center text-center gap-3">
            <button
              type="button"
              onClick={handleStartRecording}
              className="w-16 h-16 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white flex items-center justify-center transition-colors shadow-sm"
              title="Grabar audio"
            >
              <Mic className="w-7 h-7" />
            </button>
            <p className="text-[14px] text-brand-black/70">
              Tocá para grabar (hasta {MAX_DURATION_SEC}s)
            </p>
            {!hideUpload && (
              <>
                <div className="flex items-center gap-2 text-[12px] text-brand-black/45">
                  <span>o</span>
                  <button
                    type="button"
                    onClick={handleFilePick}
                    className="inline-flex items-center gap-1.5 text-brand-primary hover:underline"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    Subir un archivo
                  </button>
                </div>
                <p className="text-[11px] text-brand-black/40">
                  mp3, m4a, wav, ogg
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* RECORDING state */}
      {state === "recording" && (
        <div className="bg-white border border-neutral-200 rounded-lg p-5">
          <div className="flex flex-col items-center text-center gap-3">
            <div className="w-16 h-16 rounded-full bg-error/10 flex items-center justify-center relative">
              <span className="absolute inset-0 rounded-full bg-error/20 animate-ping" />
              <Mic className="w-7 h-7 text-error relative z-10" />
            </div>
            <p
              className={`text-[20px] font-semibold tabular-nums ${isNearLimit ? "text-error" : "text-brand-black/90"}`}
            >
              {formatDuration(recordingDuration)}
              <span className="text-[14px] font-normal text-brand-black/45">
                {" "}
                / {formatDuration(MAX_DURATION_SEC)}
              </span>
            </p>
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={handleStopRecording}
                className="inline-flex items-center gap-1.5 h-9 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[14px] font-medium rounded-md shadow-sm transition-colors"
              >
                <Square className="w-3.5 h-3.5" />
                Detener
              </button>
              <button
                type="button"
                onClick={handleCancelRecording}
                className="inline-flex items-center h-9 px-4 border border-neutral-200 bg-surface-card text-brand-black/90 text-[14px] font-medium rounded-md hover:bg-neutral-100 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* UPLOADING state */}
      {state === "uploading" && (
        <div className="bg-white border border-neutral-200 rounded-lg p-5">
          <div className="flex items-center justify-center gap-2 text-brand-black/70">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-[14px]">Subiendo audio…</span>
          </div>
        </div>
      )}

      {/* READY state — player + actions */}
      {state === "ready" && value && (
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <audio
            ref={playerRef}
            src={value.url}
            preload="metadata"
            onEnded={() => setIsPlaying(false)}
            onPause={() => setIsPlaying(false)}
            onPlay={() => setIsPlaying(true)}
            className="hidden"
          />
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={togglePlay}
              className="w-10 h-10 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white flex items-center justify-center shrink-0 transition-colors"
            >
              {isPlaying ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4 ml-0.5" />
              )}
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-[14px] text-brand-black/90 font-medium truncate">
                Audio listo
              </p>
              <p className="text-[12px] text-brand-black/50 tabular-nums">
                Duración: {formatDuration(value.durationSec)}
              </p>
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="w-8 h-8 inline-flex items-center justify-center rounded-full text-brand-black/40 hover:bg-error/10 hover:text-error transition-colors"
              title="Borrar y grabar de nuevo"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ERROR message */}
      {errorMessage && (
        <div className="bg-error/5 border border-error/20 rounded-md px-3 py-2 text-[13px] text-error">
          {errorMessage}
        </div>
      )}

      {/* Recovery action from error */}
      {state === "error" && (
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => {
              setErrorMessage(null);
              setState("idle");
            }}
            className="inline-flex items-center h-9 px-4 border border-neutral-200 bg-surface-card text-brand-black/90 text-[13px] font-medium rounded-md hover:bg-neutral-100 transition-colors"
          >
            Volver a intentar
          </button>
        </div>
      )}
    </div>
  );
};

export default DialogueAudioInput;
